import {
  ArrowRight,
  CalendarCheck2,
  CalendarRange,
  CheckCircle2,
  ClipboardCheck,
  FileSpreadsheet,
  Link2,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import PricingTable from "../components/PricingTable";
import Seo from "../components/Seo";
import WhatsAppChatWidget from "../components/WhatsAppChatWidget";
import { useAuth } from "../context/AuthContext";

const problems = [
  [
    FileSpreadsheet,
    "Steeds een ander Excel-bestand",
    "Voor je het weet werk je met een oude versie en klopt je saldo niet meer.",
  ],
  [
    ClipboardCheck,
    "Verlof aanvragen via losse berichten",
    "In e-mail en WhatsApp is al snel onduidelijk wat nog openstaat of al is goedgekeurd.",
  ],
  [
    Users,
    "Geen duidelijk jaaroverzicht",
    "Verlof, feestdagen en schoolvakanties staan op verschillende plekken.",
  ],
] as const;

const solutions = [
  [
    CalendarRange,
    "Je hele jaar in beeld",
    "Bekijk weeknummers, feestdagen, schoolvakanties en je verlof in één kalender.",
  ],
  [
    CheckCircle2,
    "Een saldo dat klopt",
    "Stel je jaarlijkse verlof en ATV in. De app rekent met de uren uit je eigen werkweek.",
  ],
  [
    ClipboardCheck,
    "Aanvragen zonder heen-en-weer mailen",
    "Medewerkers vragen verlof aan en zien meteen of het is goedgekeurd.",
  ],
  [
    Link2,
    "Ook in je eigen agenda",
    "Koppel je verlof aan Apple Agenda, Google Agenda of Outlook en bepaal zelf wat je deelt.",
  ],
] as const;

export default function LandingPage() {
  const { user } = useAuth();
  return (
    <>
      <Seo
        title="DeVerlofkaart"
        description="Bekijk je verlofsaldo, plan je vrije dagen en zet je verlof automatisch in je eigen agenda."
      />
      <section className="marketing-hero">
        <div
          className="hero-image"
          role="img"
          aria-label="Dashboard, jaarplanner en mobiele app van DeVerlofkaart"
        />
        <div className="hero-shade" aria-hidden="true" />
        <div className="marketing-container hero-content">
          <p className="eyebrow light">Voor jezelf en voor kleine teams</p>
          <h1>DeVerlofkaart</h1>
          <p>
            Zie hoeveel vrije uren je nog hebt, plan je verlof voor het hele jaar en
            bekijk je vrije dagen ook in de agenda die je al gebruikt.
          </p>
          <div className="hero-actions">
            <Link className="button primary large" to={user ? "/app" : "/registreren"}>
              {user ? "Open DeVerlofkaart" : "Gratis gebruiken"}
              <ArrowRight size={18} />
            </Link>
            <a className="button glass large" href="#product">
              Bekijk de app
            </a>
          </div>
          <span className="hero-assurance">
            <ShieldCheck size={17} /> Gratis voor persoonlijk gebruik, zonder creditcard
          </span>
        </div>
      </section>

      <section className="marketing-band problem-band">
        <div className="marketing-container">
          <div className="section-intro">
            <p className="eyebrow">Herkenbaar?</p>
            <h2>Zo raak je het overzicht kwijt.</h2>
            <p>
              Je wilt gewoon weten hoeveel uren je hebt en wanneer je vrij bent. Daar
              zou je niet voor door bestanden en oude berichten hoeven zoeken.
            </p>
          </div>
          <div className="problem-grid">
            {problems.map(([Icon, title, text]) => (
              <article key={title}>
                <Icon size={23} />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="product" className="marketing-band solution-band">
        <div className="marketing-container product-layout">
          <div className="product-copy">
            <p className="eyebrow">Alles op één plek</p>
            <h2>Je ziet je hele verlofjaar in één oogopslag.</h2>
            <p>
              Je saldo, geplande vrije dagen en belangrijke kalenderdagen staan bij
              elkaar. Je ziet ook direct wat nog op goedkeuring wacht.
            </p>
            <div className="solution-list">
              {solutions.map(([Icon, title, text]) => (
                <article key={title}>
                  <span>
                    <Icon size={20} />
                  </span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <figure className="product-shot">
            <img
              src="/images/verlofkaart-desktop-device.png"
              alt="Het echte DeVerlofkaart-dashboard op een computerscherm"
              loading="lazy"
            />
            <figcaption>Het dashboard en de jaarplanner van DeVerlofkaart.</figcaption>
          </figure>
        </div>
      </section>

      <section className="marketing-band audience-band">
        <div className="marketing-container audience-grid">
          <article>
            <span className="audience-icon">
              <CalendarCheck2 size={24} />
            </span>
            <p className="eyebrow">Voor particulieren</p>
            <h2>Je eigen verlofregistratie, gratis.</h2>
            <p>
              Vul je jaarlijkse verlof en ATV in, houd overuren bij en zie wanneer oude
              uren vervallen. Je werkpatroon bepaalt hoeveel uur een vrije dag kost.
            </p>
            <Link className="text-link" to="/functies">
              Bekijk wat je kunt doen <ArrowRight size={16} />
            </Link>
          </article>
          <article>
            <span className="audience-icon team">
              <Users size={24} />
            </span>
            <p className="eyebrow">Voor werkgevers</p>
            <h2>Iedereen weet waar een aanvraag staat.</h2>
            <p>
              Medewerkers dienen hun verlof in, managers keuren het goed en het saldo
              wordt meteen bijgewerkt. Zo blijft ook de bezetting duidelijk.
            </p>
            <Link className="text-link" to="/voor-werkgevers">
              Bekijk DeVerlofkaart voor werkgevers <ArrowRight size={16} />
            </Link>
          </article>
        </div>
      </section>

      <section className="marketing-band calendar-access-band">
        <div className="marketing-container calendar-access-layout">
          <div className="calendar-access-copy">
            <p className="eyebrow">Je agenda blijft vanzelf bijgewerkt</p>
            <h2>Je verlof op elk apparaat waarop je agenda staat.</h2>
            <p>
              Maak aparte koppelingen voor bijvoorbeeld je privéagenda en werkagenda.
              Per koppeling kies je welke statussen en verloftypen zichtbaar zijn, en of
              de afspraak details toont of alleen “Afwezig”.
            </p>
            <div className="calendar-app-list" aria-label="Ondersteunde agenda-apps">
              <span>Apple Agenda</span>
              <span>Google Agenda</span>
              <span>Outlook</span>
              <span>iCal</span>
            </div>
          </div>
          <figure className="mobile-product-shot">
            <img
              src="/images/verlofkaart-mobile-device.png"
              alt="De echte mobiele jaarplanner van DeVerlofkaart op een iPhone"
              loading="lazy"
            />
          </figure>
        </div>
      </section>

      <section className="marketing-band pricing-band">
        <div className="marketing-container">
          <div className="section-intro centered">
            <p className="eyebrow">Duidelijke pakketten</p>
            <h2>Gebruik het gratis voor jezelf. Kies Team zodra je samenwerkt.</h2>
            <p>Je ziet vooraf wat ieder pakket kost en wat erin zit.</p>
          </div>
          <PricingTable compact />
        </div>
      </section>

      <section className="marketing-band trust-band">
        <div className="marketing-container trust-row">
          <ShieldCheck size={30} />
          <div>
            <h2>Jouw gegevens blijven van jou.</h2>
            <p>
              We beveiligen accounts en verbindingen, maken back-ups en verkopen geen
              persoonsgegevens. Een geheime agendalink kun je op ieder moment vervangen
              of intrekken.
            </p>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="marketing-container">
          <h2>Klaar met zoeken naar je laatste verlofoverzicht?</h2>
          <p>
            Zet je saldo en werkweek één keer goed neer. Daarna zie je altijd waar je
            aan toe bent.
          </p>
          <Link className="button primary large" to="/registreren">
            Gebruik DeVerlofkaart gratis <ArrowRight size={18} />
          </Link>
        </div>
      </section>
      <WhatsAppChatWidget />
    </>
  );
}
